<template>
    <v-app>

        <!-- 1. Mount the global listener component to protect all application pages -->
        <maintenance-emergency-alert @emergency-triggered="onEmergencyReceived" />

        <router-view />
    </v-app>
</template>

<script>

import { MaintenanceTracker } from '@/helpers/system-maintenance-tracker'

export default {
    name: 'app',
    components: {
        'maintenance-emergency-alert': () => import('@/components/PublicNotifications/MaintenanceEmergencyAlert')
    },
    data() {
        return {
            waitingServiceWorker: null,
            refreshing: false,

            onControllerChange: null,
            onDownloading: null,
            onCached: null,
            onUpdated: null,
            onReady: null,

             // Maintenance Tracker reference storage instance
            maintenanceTracker: null 
        }
    },

    methods: {
        // Handle global maintenance routing states
        handleMaintenanceState(state) {
            const currentPath = this.$route.path

            if (state.status === 'ACTIVE') {
                if (currentPath !== '/maintenance') {
                    this.$router.push({
                        path: '/maintenance',
                        // This updates the URL to: /maintenance?endTime=today+at+12%3A15+PM
                        query: { endTime: state.formattedEndTime } 
                    }).catch(() => {})
                }
            } else if (state.status === 'EXPIRED') {
                if (currentPath === '/maintenance') {
                    this.$router.push('/home').catch(() => {})
                }
            }
        },
        onEmergencyReceived(notificationItem) {
            console.log('onEmergencyReceived - from App.vue')
            localStorage.setItem('publicNotifications', JSON.stringify([notificationItem]))

            if (this.maintenanceTracker) {
                console.log('onEmergencyReceived - calling evaluateMaintenance')
                this.maintenanceTracker.evaluateMaintenance([notificationItem])
            }
        },
        
        // ============================
        // EVENTS
        // ============================

        handleDownloading() {
            console.log('handleDownloading')
            this.waitingServiceWorker = null

            if (this.isFirstTime) {
                localStorage.setItem('sw_install_state', 'installing')

                window.dispatchEvent(
                    new CustomEvent('pwa-state-change', {
                        detail: { state: 'firstVisit' }
                    })
                )
            } else {
                // If they have a flag or a userId, ensure the flag is explicitly saved for future visits
                localStorage.setItem('hasVisitedSweldoNow', 'true')
                
                window.dispatchEvent(new CustomEvent('pwa-state-change', {
                    detail: { state: 'downloading' }
                }))
            }
        },

        handleUpdated(event) {
            console.log('handleUpdated')
            
            // Fallback to event context if detail is empty depending on your SW registrar
            this.waitingServiceWorker = event.detail || event 

            window.dispatchEvent(new CustomEvent('pwa-state-change', {
                detail: { state: 'updateReady' }
            }))

            this.applyUpdate()
        },

        handleFirstTimeCache() {
            console.log('handleFirstTimeCache')

            if (!this.isFirstTime)
                return

            localStorage.setItem('sw_install_state', 'complete')
            localStorage.setItem('hasVisitedSweldoNow', 'true')

            window.location.reload()
        },

        handleReady() {
            console.log('handleReady')

            window.dispatchEvent(
                new CustomEvent('pwa-state-change', {
                    detail: { state: 'ready' }
                })
            )
        },

        // ============================
        // AUTO UPDATE FLOW
        // ============================
        applyUpdate() {
            console.log('applyUpdate')

            if (!this.waitingServiceWorker || !this.waitingServiceWorker.waiting) 
                return

            window.dispatchEvent(new CustomEvent('pwa-state-change', {
                detail: { state: 'applying' }
            }))

            this.onControllerChange = () => {
                if (this.refreshing) return
                this.refreshing = true
                window.location.reload()
            }

            navigator.serviceWorker.addEventListener(
                'controllerchange',
                this.onControllerChange
            )

            this.waitingServiceWorker.waiting.postMessage({
                type: 'SKIP_WAITING'
            })
        },
    },
    created() {
        // 1. Execute system maintenance check immediately as the top priority
        this.maintenanceTracker = new MaintenanceTracker((state) => {
            this.handleMaintenanceState(state)
        })
        this.maintenanceTracker.checkStatus()
        
        // 2. Set up PWA automated caching/updating functions afterward
        this.onDownloading = this.handleDownloading
        this.onUpdated = this.handleUpdated
        this.onCached = this.handleFirstTimeCache
        this.onReady = this.handleReady

        window.addEventListener('pwa-update-downloading', this.onDownloading)
        window.addEventListener('pwa-update-ready', this.onUpdated)
        window.addEventListener('pwa-update-cached', this.onCached)
        window.addEventListener('pwa-ready', this.onReady)
    },
    computed: {
        isFirstTime() {
            return !localStorage.getItem('hasVisitedSweldoNow')
        }
    },
    watch: {
        // Crucial: If user navigates routes manually, verify maintenance rules instantly
        $route() {
            if (this.maintenanceTracker) {
                this.maintenanceTracker.checkStatus()
            }
        }
    },
    beforeDestroy() {
        window.removeEventListener('pwa-update-downloading', this.onDownloading)
        window.removeEventListener('pwa-update-ready', this.onUpdated)
        window.removeEventListener('pwa-update-cached', this.onCached)
        window.removeEventListener('pwa-ready', this.onReady)

        if (this.onControllerChange) {
            navigator.serviceWorker.removeEventListener(
                'controllerchange',
                this.onControllerChange
            )
        }
    }
}
</script>